import React, { useState, useEffect } from 'react';
import {
  Typography, Button, Box, Alert, CircularProgress, TextField,
} from '@mui/material';
import ModalWrapper from '../components/ModalWrapper';
import StepWizard from '../components/StepWizard';
import { apiFetch } from '../apiFetch';

interface TwoFactorSetupModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  purpose: 'login' | 'admin';
}

const TwoFactorSetupModal: React.FC<TwoFactorSetupModalProps> = ({
  open,
  onClose,
  onSuccess,
  purpose,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;
  const steps = ['Scan QR Code', 'Verify'];

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setQrCode(null);
      setSecret(null);
      setVerificationCode('');
      setError(null);
      fetchSetupData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchSetupData = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await apiFetch(`${apiUrl}/api/2fa/setup/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ purpose }),
      });

      setQrCode(data.qr_code);
      setSecret(data.secret);
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'Failed to generate 2FA setup.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setVerifying(true);
    setError(null);

    try {
      await apiFetch(`${apiUrl}/api/2fa/verify-setup/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          purpose,
          code: verificationCode,
        }),
      });

      onSuccess();
      onClose();
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'Verification failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
    setError(null);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError(null);
  };

  const title = purpose === 'admin'
    ? 'Set Up Admin Two-Factor Authentication'
    : 'Set Up Two-Factor Authentication';

  return (
    <ModalWrapper open={open} onClose={onClose}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>

      <StepWizard steps={steps} activeStep={activeStep} sx={{ my: 3 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {activeStep === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          {loading ? (
            <CircularProgress />
          ) : qrCode ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
              </Typography>
              <Box
                component="img"
                src={qrCode}
                alt="2FA QR Code"
                sx={{
                  width: 200,
                  height: 200,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                Or enter this code manually:
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'monospace',
                  backgroundColor: 'action.hover',
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  letterSpacing: '0.1em',
                  userSelect: 'all',
                }}
              >
                {secret}
              </Typography>
            </>
          ) : (
            <Typography color="error">Failed to load QR code.</Typography>
          )}
        </Box>
      )}

      {activeStep === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            Enter the 6-digit code from your authenticator app to verify setup.
          </Typography>
          <TextField
            label="Verification Code"
            variant="outlined"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            inputProps={{
              maxLength: 6,
              inputMode: 'numeric',
              pattern: '[0-9]*',
              style: { letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.5rem' },
            }}
            sx={{ width: 200 }}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && verificationCode.length === 6) {
                handleVerify();
              }
            }}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          variant="outlined"
          onClick={activeStep === 0 ? onClose : handleBack}
          disabled={verifying}
        >
          {activeStep === 0 ? 'Cancel' : 'Back'}
        </Button>

        {activeStep === 0 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading || !qrCode}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleVerify}
            disabled={verifying || verificationCode.length !== 6}
          >
            {verifying ? <CircularProgress size={24} /> : 'Verify & Enable'}
          </Button>
        )}
      </Box>
    </ModalWrapper>
  );
};

export default TwoFactorSetupModal;
